/**
 * Имя человека в ERP — «Имя [Отчество] Фамилия» везде (ТЗ 0111, решение
 * владельца 15.10.2026). Одно место на порядок слов, сортировку и поиск:
 * до этого каждый экран клеил имя сам, и половина печатала «Фамилия Имя».
 */
export interface IPersonNameParts {
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
}

const clean = (value?: string | null): string => (value || '').trim();

/** «Имя Отчество Фамилия»; пустые части пропускаются. Нет ничего — `fallback`. */
export const personName = (
  person?: IPersonNameParts | null,
  fallback = '',
): string =>
  [person?.firstName, person?.middleName, person?.lastName]
    .map(clean)
    .filter(Boolean)
    .join(' ') || fallback;

const cmp = (a?: string | null, b?: string | null): number =>
  clean(a).localeCompare(clean(b), undefined, { sensitivity: 'base' });

/** Сортировка по имени, затем по фамилии (затем по отчеству). */
export const compareByPersonName = (
  a?: IPersonNameParts | null,
  b?: IPersonNameParts | null,
): number =>
  cmp(a?.firstName, b?.firstName) ||
  cmp(a?.lastName, b?.lastName) ||
  cmp(a?.middleName, b?.middleName);

/**
 * Свернуть регистр и диакритику: «Michał» ищется и как «michal».
 * `ł` в Unicode не раскладывается — заменяем руками.
 */
export const foldSearch = (value: string): string =>
  value.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/ł/g, 'l');

type THaystack =
  | IPersonNameParts
  | string
  | null
  | undefined
  | Array<string | null | undefined>;

const haystackText = (haystack: THaystack): string => {
  if (!haystack) return '';
  if (typeof haystack === 'string') return haystack;
  if (Array.isArray(haystack)) return haystack.filter(Boolean).join(' ');
  return [haystack.firstName, haystack.middleName, haystack.lastName]
    .filter(Boolean)
    .join(' ');
};

/**
 * Поиск по имени в любом порядке слов: «Jaglarz Michał» и «Michał Jaglarz»
 * находят одно и то же. Каждое слово запроса должно найтись где-то в тексте
 * (подстрокой, без учёта регистра и диакритики). Пустой запрос — совпадение.
 * `haystack` — части имени, готовая строка или список полей (имя, почта,
 * телефон…), которые ищутся вместе.
 */
export const matchesPersonQuery = (
  haystack: THaystack,
  query: string,
): boolean => {
  const words = foldSearch(query).split(/\s+/).filter(Boolean);
  if (!words.length) return true;
  const text = foldSearch(haystackText(haystack));
  return words.every((word) => text.includes(word));
};
